import {
  DEFAULT_LAYOUT_SETTINGS,
  Filler,
  PublisherPaginationSettings,
  HeaderSettingsConfigWithHeight
} from '../../types/publisherPaginationSettings';
import { ESnapshotExists, ETemplate } from '../../types';
import { wrapError, wrapSuccess } from '../../types/responses';
import {
  OptimizerHyperparams,
  AutoPaginationRequestLayout,
  AutoLayoutRequestElement,
  LayoutBlockFixedElement
} from '../../types/api';

/**
 * Calculate max whitespace based on layout size using percentage of layout area
 * Uses linear scaling based on the configured percentage
 */
function calculateMaxWhitespaceForLayout(
  layoutWidth: number,
  layoutHeight: number,
  hyperparams: OptimizerHyperparams
): number {
  const layoutArea = layoutWidth * layoutHeight;
  const allowedWhitespace =
    layoutArea * (hyperparams.max_whitespace_percentage / 100);
  const allowedFillers = layoutArea * (hyperparams.max_filler_percentage / 100);
  return Math.floor(allowedWhitespace + allowedFillers);
}

const getTotalColumnInches = (orderBlocks: AutoLayoutRequestElement[]) => {
  return orderBlocks.reduce((acc, notice) => {
    const biggestVariant = notice.variants.reduce((biggest, variant) => {
      return Math.max(biggest, variant.height * variant.width);
    }, 0);
    return acc + biggestVariant;
  }, 0);
};

export const layoutIsValidForOrders = (
  layout: AutoPaginationRequestLayout,
  template: ESnapshotExists<ETemplate>,
  orderBlocks: AutoLayoutRequestElement[],
  hyperparams: OptimizerHyperparams,
  publisherPaginationSettings: ESnapshotExists<PublisherPaginationSettings>
) => {
  const totalFixedHeightArea = layout.fixed_elements.reduce((acc, element) => {
    return acc + element.height;
  }, 0);
  const totalOrderArea = getTotalColumnInches(orderBlocks);

  const potentialWhitespace =
    layout.width * layout.height - (totalOrderArea + totalFixedHeightArea);

  // if the whitespace required is too much, return false
  const maxWhitespaceForLayout = calculateMaxWhitespaceForLayout(
    layout.width,
    layout.height,
    hyperparams
  );
  if (potentialWhitespace > maxWhitespaceForLayout) {
    return false;
  }

  const widthIsFeasible = orderBlocks.some(block =>
    block.variants.some(variant => variant.width <= layout.width)
  );
  if (!widthIsFeasible) {
    return false;
  }

  const templatePageHeight = template.data().styles?.pageHeight ?? 0;
  const isTooTall = layout.height > templatePageHeight;
  if (isTooTall) {
    return false;
  }

  const layoutColumns = template.data().styles?.columnCount ?? 0;
  const isTooWide = layout.width > layoutColumns;
  if (isTooWide) {
    return false;
  }

  // check if the layout is too skinny based on the custom min column settings
  const minColumnWidth =
    publisherPaginationSettings?.data().layoutSettings?.minColumns;
  if (minColumnWidth && layout.width < minColumnWidth) {
    return false;
  }

  return true;
};

const fillerIsValidForLayout = (
  filler: Filler,
  layout: {
    columns: number;
    columnRangeInOrders?: { min: number; max: number };
  }
) => {
  if (filler.columns === 3 && layout.columns === 4) {
    return false;
  }

  // Make sure the fillers line up with existing column widths for the ads on the page
  if (layout.columnRangeInOrders) {
    if (filler.columns > layout.columnRangeInOrders.max) {
      return false;
    }

    if (filler.columns < layout.columnRangeInOrders.min) {
      return false;
    }
  }
  return true;
};

export const getPotentialPublisherLayouts = (
  publisherPaginationSettings: ESnapshotExists<PublisherPaginationSettings>,
  template: ESnapshotExists<ETemplate>
) => {
  const { layoutSettings } = publisherPaginationSettings.data();
  const { minHeight, heightIncrements } =
    layoutSettings || DEFAULT_LAYOUT_SETTINGS;
  const layouts: AutoPaginationRequestLayout[] = [];

  const templateHeight = template.data().styles?.pageHeight;
  if (!templateHeight) {
    return wrapError(new Error('Template height not found'));
  }
  const columns = template.data().styles?.columnCount ?? 1;

  for (
    let layoutHeight = minHeight;
    layoutHeight < templateHeight;
    layoutHeight += heightIncrements
  ) {
    for (let layoutWidth = 1; layoutWidth <= columns; layoutWidth++) {
      layouts.push({
        id: `auto-paginate-${layoutHeight}-${layoutWidth}-height`,
        width: layoutWidth,
        height: layoutHeight,
        fixed_elements: [],
        max_whitespace: 0,
        optional_elements: []
      });
    }
  }
  return wrapSuccess(layouts);
};

const getFixedElementsForColumnWidth = (
  headerSettings: HeaderSettingsConfigWithHeight | null | undefined,
  { columns }: { columns: number }
) => {
  if (!headerSettings) {
    return [];
  }
  const fixedElements: LayoutBlockFixedElement[] = [];

  const relevantHeader = headerSettings.headers.find(header =>
    header.applyToColumns.includes(columns)
  );
  if (relevantHeader) {
    for (let i = 0; i < columns; i += relevantHeader.width) {
      fixedElements.push({
        x: i,
        y: 0,
        width: relevantHeader.width,
        height: relevantHeader.height,
        id: relevantHeader.id
      });
    }
  }
  return fixedElements;
};

export const getLayoutsForAutoPaginationRequest = async (
  publisherPaginationSettings: ESnapshotExists<PublisherPaginationSettings>,
  hyperparams: OptimizerHyperparams,
  template: ESnapshotExists<ETemplate>,
  orderBlocks: AutoLayoutRequestElement[],
  fillers: Filler[],
  headerSettings: HeaderSettingsConfigWithHeight | null | undefined
) => {
  if (!orderBlocks.length) {
    return wrapError(
      new Error('No order blocks provided, so no layouts can be generated')
    );
  }
  const maxFillersForPublisherLayouts = 1;

  const [potentialLayoutsError, potentialLayouts] =
    getPotentialPublisherLayouts(publisherPaginationSettings, template);
  if (potentialLayoutsError) {
    return wrapError(potentialLayoutsError);
  }

  const columnRangeInOrders = orderBlocks.reduce(
    (range, block) => {
      const blockMinColumns = Math.min(...block.variants.map(v => v.width));
      const blockMaxColumns = Math.max(...block.variants.map(v => v.width));
      return {
        min: Math.min(range.min, blockMinColumns),
        max: Math.max(range.max, blockMaxColumns)
      };
    },
    { min: Infinity, max: -Infinity }
  );

  const layouts: AutoPaginationRequestLayout[] = potentialLayouts.map(
    allowedLayout => {
      const fixedElements = getFixedElementsForColumnWidth(headerSettings, {
        columns: allowedLayout.width
      });

      const layout = {
        id: allowedLayout.id,
        width: allowedLayout.width,
        height: allowedLayout.height,
        fixed_elements: fixedElements,
        max_whitespace: calculateMaxWhitespaceForLayout(
          allowedLayout.width,
          allowedLayout.height,
          hyperparams
        ),
        optional_elements: fillers
          .filter(filler =>
            fillerIsValidForLayout(filler, {
              columns: allowedLayout.width,
              columnRangeInOrders
            })
          )
          .flatMap(filler =>
            Array.from({ length: maxFillersForPublisherLayouts }, (_, i) => ({
              ...filler,
              width: filler.columns,
              id: `${filler.name}-${allowedLayout.id}-${filler.id}-${i}`
            }))
          )
      };
      return layout;
    }
  );

  // dynamically compute the max number of full page column layouts to consider
  const fullPageLayoutColumnInches =
    Math.floor(template.data().styles?.pageHeight ?? 0) *
    (template.data().styles?.columnCount ?? 1);
  const totalColumnInches = getTotalColumnInches(orderBlocks);
  const fullPageLayoutsToInclude = Math.ceil(
    totalColumnInches / fullPageLayoutColumnInches
  );

  // if we have just one order and no fixed elements (headers), create a custom-sized template for it
  // to guarantee a valid layout exists even for publishers with large minHeight or coarse heightIncrements.
  // if there are fixed elements, the notice + header height should fit within the standard incremental layouts.
  if (orderBlocks.length === 1) {
    const [onlyBlock] = orderBlocks;
    const defaultVariant = onlyBlock.variants[0];
    const fixedElements = getFixedElementsForColumnWidth(headerSettings, {
      columns: defaultVariant.width
    });
    if (!fixedElements.length) {
      layouts.push({
        id: `custom-sized-order-${onlyBlock.id}`,
        width: defaultVariant.width,
        height: defaultVariant.height,
        max_whitespace: 1,
        fixed_elements: [],
        optional_elements: []
      });
    }
  }

  for (let i = 0; i < fullPageLayoutsToInclude; i++) {
    const fixedElements = getFixedElementsForColumnWidth(headerSettings, {
      columns: template.data().styles?.columnCount ?? 1
    });

    layouts.push({
      id: `auto-paginate-${i}-full-page`,
      width: template.data().styles?.columnCount ?? 1,
      height: Math.floor(template.data().styles?.pageHeight ?? 0),
      max_whitespace: calculateMaxWhitespaceForLayout(
        template.data().styles?.columnCount ?? 1,
        Math.floor(template.data().styles?.pageHeight ?? 0),
        hyperparams
      ),
      fixed_elements: fixedElements,
      optional_elements: fillers
        .filter(filler =>
          fillerIsValidForLayout(filler, {
            columns: template.data().styles?.columnCount ?? 1
          })
        )
        .flatMap(filler =>
          Array.from({ length: filler.maxOccurrences }, (_, j) => ({
            ...filler,
            width: filler.columns,
            id: `${filler.name}-big-${i}-${filler.id}-${j}`
          }))
        )
    });
  }
  for (let col = 1; col < (template?.data()?.styles?.columnCount || 1); col++) {
    const fixedElements = getFixedElementsForColumnWidth(headerSettings, {
      columns: col
    });

    layouts.push({
      id: `auto-paginate-${col}-col`,
      width: col,
      height: Math.floor(template?.data()?.styles?.pageHeight ?? 0),
      max_whitespace: calculateMaxWhitespaceForLayout(
        col,
        Math.floor(template?.data()?.styles?.pageHeight ?? 0),
        hyperparams
      ),
      fixed_elements: fixedElements,
      optional_elements: fillers
        .filter(filler => fillerIsValidForLayout(filler, { columns: col }))
        .flatMap(filler =>
          Array.from({ length: filler.maxOccurrences }, (_, j) => ({
            ...filler,
            width: filler.columns,
            id: `${filler.name}-small-${col}-${filler.id}-${j}`
          }))
        )
    });
  }

  const possibleLayouts = layouts.filter(layout => {
    return layoutIsValidForOrders(
      layout,
      template,
      orderBlocks,
      hyperparams,
      publisherPaginationSettings
    );
  });

  if (!possibleLayouts.length) {
    return wrapError(
      new Error(
        'No layouts found that could work with the request. This probably means that you need a new layout size for small publication issues to be able to auto-paginate.'
      )
    );
  }

  return wrapSuccess(possibleLayouts);
};
